import { ImagesAuthenticationOnboardingOnboardingBuyerInformationWebp } from '@/assets/images_authentication_onboarding.assets';
import { ImagesPressLogosGoldBlueSpiritoryLogoSvg } from '@/assets/images_press_logos_gold-blue.assets';
import DefaultButton from '@/components/common/default/DefaultButton';
import {
  OnBoardingInformationFaqItemProps,
  OnBoardingInformationProps,
} from '@/types/props/components/onboarding';
import clsx from 'clsx';
import Image from 'next/image';
import { useState } from 'react';

export default function OnBoardingInformation({ items }: OnBoardingInformationProps) {
  return (
    <div className="tw-relative tw-h-full tw-w-full">
      <Image
        aria-hidden="true"
        alt="Onboarding Background Image"
        width={1920}
        height={1080}
        src={ImagesAuthenticationOnboardingOnboardingBuyerInformationWebp}
        className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover"
      />
      <div
        aria-hidden="true"
        className="tw-absolute tw-inset-0 tw-z-10 tw-h-full tw-w-full tw-bg-[#1C5CEBB2]"
      />
      <div className="tw-relative tw-z-50 tw-flex tw-h-full tw-w-full tw-flex-col tw-gap-6 tw-px-8 tw-py-10">
        {items.map((item, index) => {
          if (item.type === 'caption') {
            return (
              <div
                className={clsx(
                  'tw-flex tw-items-start tw-gap-3',
                  index % 2 === 0 ? 'tw-flex-row-reverse' : 'tw-flex-row'
                )}
                key={item.caption as string}
              >
                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
                  <Image
                    src={ImagesPressLogosGoldBlueSpiritoryLogoSvg}
                    alt="Spiritory Logo"
                    width={36}
                    height={36}
                  />
                </div>
                <div
                  className={clsx('tw-rounded-bl-xl tw-rounded-br-xl tw-bg-white tw-p-4', {
                    ['tw-rounded-tr-xl']: index % 2 !== 0,
                    ['tw-rounded-tl-xl']: index % 2 === 0,
                  })}
                >
                  <p className="tw-flex tw-flex-col tw-gap-1 tw-font-plusJakartaSans tw-text-lg tw-font-medium tw-leading-[25.2px] tw-text-slate-gray max-nine:tw-text-xs">
                    <strong className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-leading-[30.24px] tw-text-textBlack max-nine:!tw-text-base">
                      {item.caption as string}
                    </strong>
                    {item.body as string}
                  </p>
                </div>
              </div>
            );
          }

          if (item.type === 'question') {
            return (
              <div
                className={clsx(
                  'tw-flex tw-items-start tw-gap-3',
                  index % 2 === 0 ? 'tw-flex-row-reverse' : 'tw-flex-row'
                )}
                key={JSON.stringify(item.items)}
              >
                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-white">
                  <Image
                    src={ImagesPressLogosGoldBlueSpiritoryLogoSvg}
                    alt="Spiritory Logo"
                    width={36}
                    height={36}
                  />
                </div>
                <div
                  className={clsx(
                    'tw-w-full tw-rounded-bl-xl tw-rounded-br-xl tw-bg-white tw-p-4',
                    {
                      ['tw-rounded-tr-xl']: index % 2 !== 0,
                      ['tw-rounded-tl-xl']: index % 2 === 0,
                    }
                  )}
                >
                  <strong className="tw-font-plusJakartaSans tw-text-2xl tw-font-bold tw-leading-[30.24px] tw-text-textBlack max-nine:!tw-text-base">
                    FAQ
                  </strong>
                  <ul className="tw-pt-2">
                    {item.items.map(faqItem => (
                      <OnBoardingInformationFaqItem key={JSON.stringify(faqItem)} {...faqItem} />
                    ))}
                  </ul>
                </div>
              </div>
            );
          }
        })}
      </div>
    </div>
  );
}

function OnBoardingInformationFaqItem({ question, answer }: OnBoardingInformationFaqItemProps) {
  const [isAnswerVisible, setIsAnswerVisible] = useState(false);
  return (
    <li>
      <span className="tw-font-plusJakartaSans tw-text-lg tw-font-medium tw-leading-[25.2px] tw-text-slate-gray">
        {question}
      </span>
      <br />
      {isAnswerVisible === false && (
        <DefaultButton
          className="tw-text-sm tw-text-main tw-underline tw-underline-offset-4"
          onClick={() => setIsAnswerVisible(true)}
        >
          Show Answer
        </DefaultButton>
      )}
      {isAnswerVisible === true && (
        <p className="tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-leading-[25.2px] tw-text-slate-gray">
          {answer}
          <br />
          <DefaultButton
            className="tw-text-main tw-underline tw-underline-offset-4"
            onClick={() => setIsAnswerVisible(false)}
          >
            Hide Answer
          </DefaultButton>
        </p>
      )}
    </li>
  );
}
